import { onMounted, onUnmounted, type Ref, watch } from 'vue'

import { type PracticeListItem } from '@/composables/useTextCategories'
import { GRAPHQL_URL } from '@/constants/appConstants'
import { extractContentIds, HEARTBEAT_INTERVAL_MS, IDLE_THRESHOLD_MS, INTERACTION_THROTTLE_MS } from '@/constants/practiceConstants'
import EndPracticeSession from '@/mutations/EndPracticeSession.graphql'
import PracticeHeartbeat from '@/mutations/PracticeHeartbeat.graphql'
import store from '@/store'
import { mutateSilently } from '@/store/mutationHelpers'
const INTERACTION_EVENTS = ['mousemove', 'keydown', 'scroll', 'touchstart'] as const

/**
 * Inline GraphQL string for the sendBeacon call on page unload.
 * sendBeacon cannot use Apollo Client, so we send a raw JSON payload.
 * Must match src/mutations/EndPracticeSession.graphql (operation name omitted for brevity).
 */
const END_SESSION_BEACON_QUERY = 'mutation { endPracticeSession { ok } }'

/**
 * Sends periodic heartbeat mutations while the student is actively practicing.
 * Pauses when the tab is hidden or the user is idle for IDLE_THRESHOLD_MS.
 * Ends the session on unmount or tab close.
 */
export function usePracticeTracking(selectedItem: Ref<PracticeListItem | null>): void {
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null
  let lastInteraction = Date.now()
  let lastThrottledUpdate = 0

  function isTrackingEnabled(): boolean {
    return store.getters.student.barMitzvah?.practiceTracking ?? false
  }

  function isIdle(): boolean {
    return Date.now() - lastInteraction > IDLE_THRESHOLD_MS
  }

  function sendHeartbeat(): void {
    if (!isTrackingEnabled() || isIdle() || document.visibilityState === 'hidden' || !selectedItem.value) {
      return
    }
    const item = selectedItem.value
    const { aliyahId, haftarahId, sentenceGroupId } = extractContentIds(item.aliyah, item.haftarah, null, item.sentenceGroup)
    // Heartbeat failures are non-critical
    mutateSilently(PracticeHeartbeat, 'practiceHeartbeat', { aliyahId, haftarahId, sentenceGroupId })
  }

  function sendEndSession(): void {
    // Best-effort on teardown
    mutateSilently(EndPracticeSession, 'endPracticeSession')
  }

  function onInteraction(): void {
    const now = Date.now()
    if (now - lastThrottledUpdate < INTERACTION_THROTTLE_MS) {
      return
    }
    lastThrottledUpdate = now
    lastInteraction = now
  }

  function onVisibilityChange(): void {
    if (document.visibilityState === 'hidden') {
      // Tab hidden — end session immediately
      sendEndSession()
    } else {
      // Tab visible again — if not idle, send a heartbeat to resume
      lastInteraction = Date.now()
      sendHeartbeat()
    }
  }

  function onBeforeUnload(): void {
    // Best-effort end session via sendBeacon (can't use Apollo — see END_SESSION_BEACON_QUERY)
    const body = JSON.stringify({ query: END_SESSION_BEACON_QUERY })
    navigator.sendBeacon(GRAPHQL_URL, new Blob([body], { type: 'application/json' }))
  }

  function startTracking(): void {
    if (heartbeatTimer) {
      return
    }
    heartbeatTimer = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS)
    // Send initial heartbeat immediately
    sendHeartbeat()

    for (const event of INTERACTION_EVENTS) {
      document.addEventListener(event, onInteraction, { passive: true })
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('beforeunload', onBeforeUnload)
  }

  function stopTracking(): void {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer)
      heartbeatTimer = null
    }
    for (const event of INTERACTION_EVENTS) {
      document.removeEventListener(event, onInteraction)
    }
    document.removeEventListener('visibilitychange', onVisibilityChange)
    window.removeEventListener('beforeunload', onBeforeUnload)
  }

  onMounted(() => {
    // Wait for barMitzvah data to load before starting. Immediate, because it may have loaded already;
    // startTracking is idempotent.
    watch(
      () => store.getters.student.barMitzvah,
      (barMitzvah) => {
        if (barMitzvah?.practiceTracking) {
          startTracking()
        }
      },
      { immediate: true },
    )
  })

  onUnmounted(() => {
    stopTracking()
    sendEndSession()
  })
}
